<script setup lang="ts">
  import { Columns3 } from "lucide-vue-next";
  import { Button } from "@/components/ui/button";
  import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuCheckboxItem,
  } from "@/components/ui/dropdown-menu";
  import type { ToggleableColumn } from "./types";

  const props = withDefaults(
    defineProps<{
      columns: ToggleableColumn[];
      compact?: boolean;
    }>(),
    { compact: true },
  );

  const visibility = defineModel<Record<string, boolean>>({ required: true });

  const isVisible = (column: ToggleableColumn) => visibility.value[column.key] ?? !column.hiddenByDefault;

  const toggle = (column: ToggleableColumn) => {
    visibility.value = { ...visibility.value, [column.key]: !isVisible(column) };
  };
</script>

<template>
  <DropdownMenu>
    <DropdownMenuTrigger as-child>
      <Button
        v-if="props.compact"
        variant="ghost"
        size="icon"
        class="text-muted-foreground hover:text-primary"
        aria-label="Columns"
        title="Columns">
        <Columns3
          :stroke-width="1.8"
          class="size-4" />
      </Button>
      <Button
        v-else
        variant="outline"
        class="gap-2 bg-background font-semibold text-primary">
        <Columns3
          :stroke-width="1.8"
          class="size-3.5" />
        Columns
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent
      align="end"
      class="max-h-80 w-56 overflow-y-auto">
      <DropdownMenuCheckboxItem
        v-for="column in props.columns"
        :key="column.key"
        :model-value="isVisible(column)"
        @select.prevent="toggle(column)">
        {{ column.label }}
      </DropdownMenuCheckboxItem>
    </DropdownMenuContent>
  </DropdownMenu>
</template>
